import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { capitalizeFirstLetter, uniqueId } from "../common/methods";
import { useStaticImages } from "../hooks/useStaticImages";
import { NoResultsFound } from "./common/NoResultsFound";
import moment from "moment";
import { nanoid } from "nanoid";
import { determineUserCity } from "../utils/userLocation";
import { nearbyDestinations, countryToCityMapping, DEFAULT_CITY } from "../utils/locationData";

// Aggressive lazy loading for ALL images to ensure heading is LCP
const useLazyBackgroundImage = (src, isMobile, index) => {
  // ALL images should be lazy loaded to ensure heading is LCP
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    // Add delay to ensure heading renders first as LCP
    const loadTimeout = setTimeout(() => {
      const img = new Image();
      img.onload = () => setIsLoaded(true);
      img.src = src;
    }, index * 200); // Stagger loading

    return () => clearTimeout(loadTimeout);
  }, [src, isVisible, index]);

  return { isLoaded, imgRef };
};

export const DeferredPopularContent = ({ 
  isNotMobile, 
  isMobileDevice, 
  staticDestinations, 
  translation, 
  navigate,
  topFlightsHome,
  setTopFlightsHome,
  returnOrOneWayTopLocations,
  setReturnOrOneWayTopLocations,
  topLocationsPassengers,
  setTopLocationsPassengers,
  userCity,
  setUserCity
}) => {
  const [toggleOneWay, setToggleOneWay] = useState(false);
  const [togglePassengers, setTogglePassengers] = useState(false);

  // Load user city after component mounts with increased delay
  useEffect(() => {
    const loadUserCity = async () => {
      try {
        const city = await determineUserCity({ 
          nearbyDestinations, 
          countryToCityMapping, 
          defaultCity: DEFAULT_CITY 
        });
        setUserCity(city);
      } catch (error) {
        // Removed console.error for production
      }
    };
    
    // Increased delay to ensure heading renders first as LCP
    setTimeout(loadUserCity, 500);
  }, [setUserCity]);

  // Memoize image data with working URLs
  const topLocationsGPT = useMemo(() => [
    {
      cityTo: "Istanbul",
      cityName: "Istanbul",
      price: 150,
      image: isMobileDevice 
        ? "https://images.pexels.com/photos/8017047/pexels-photo-8017047.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1"
        : "https://images.pexels.com/photos/8017047/pexels-photo-8017047.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1",
      location: "Istanbul",
      flyTo:'IST'
    },
    {
      cityTo: "Dusseldorf",
      cityName: "Dusseldorf",
      price: 200,
      image: isMobileDevice
        ? "https://images.pexels.com/photos/25034253/pexels-photo-25034253/free-photo-of-view-of-the-rhine-tower-and-part-of-the-rheinkniebrucke-in-dusseldorf-germany.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1"
        : "https://images.pexels.com/photos/25034253/pexels-photo-25034253/free-photo-of-view-of-the-rhine-tower-and-part-of-the-rheinkniebrucke-in-dusseldorf-germany.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1",
      location: "Dusseldorf",
      flyTo:'DUS'
    },
    {
      cityTo: "Milan",
      cityName: "Milan",
      price: 180,
      image: isMobileDevice
        ? "https://images.pexels.com/photos/1797161/pexels-photo-1797161.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1"
        : "https://images.pexels.com/photos/1797161/pexels-photo-1797161.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1",
      location: "Milan",
      flyTo:'MXP'
    },
    {
      cityTo: "Vienna",
      cityName: "Vienna",
      price: 160,
      image: isMobileDevice
        ? "https://images.pexels.com/photos/1486222/pexels-photo-1486222.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1"
        : "https://images.pexels.com/photos/1486222/pexels-photo-1486222.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1",
      location: "Vienna",
      flyTo:'VIE'
    },
    {
      cityTo: "Amsterdam",
      cityName: "Amsterdam",
      price: 190,
      image: isMobileDevice
        ? "https://images.pexels.com/photos/460672/pexels-photo-460672.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1"
        : "https://images.pexels.com/photos/460672/pexels-photo-460672.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1",
      location: "Amsterdam",
      flyTo:'AMS'
    },
    {
      cityTo: "Basel",
      cityName: "Basel",
      price: 170,
      image: isMobileDevice
        ? "https://images.pexels.com/photos/20495253/pexels-photo-20495253/free-photo-of-bird-eyes-view-of-basel-minster-in-basel-in-switzerland.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1"
        : "https://images.pexels.com/photos/20495253/pexels-photo-20495253/free-photo-of-bird-eyes-view-of-basel-minster-in-basel-in-switzerland.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1",
      location: "Basel",
      flyTo:'BSL'
    }
  ], [isMobileDevice]);

  const formattedTopLocations = useMemo(() => topLocationsGPT?.map((location) => ({ 
    city: location.cityTo,
    cityName: location.cityTo,
    price: Math.floor(location?.price),
    image: location.image,
    flyFrom: 'PRN',
    flyTo: location.flyTo,
    topLocation: location,
  })), [topLocationsGPT]);

  // Cache images after component mounts with increased delay
  useEffect(() => {
    const cacheDestinationImages = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const imageUrls = formattedTopLocations.map(location => location.image);
          
          const messageChannel = new MessageChannel();
          messageChannel.port1.onmessage = (event) => {
            // Removed console.log for production
            if (!event.data.success) {
              // Removed console.error for production
            }
          };
          
          registration.active?.postMessage({
            type: 'CACHE_IMAGES',
            images: imageUrls
          }, [messageChannel.port2]);
        } catch (error) {
          // Removed console.error for production
        }
      }
    };

    if (formattedTopLocations.length > 0) {
      // Increased delay to avoid blocking LCP
      setTimeout(cacheDestinationImages, 2000);
    }
  }, [formattedTopLocations]);

  // Event handlers
  const handleToggleOneWay = useCallback(() => {
    setToggleOneWay(prev => !prev);
  }, []);

  const handleTogglePassengers = useCallback(() => {
    setTogglePassengers(prev => !prev);
  }, []);

  const handleSetPopular = useCallback(() => {
    setTopFlightsHome("popular", true);
  }, [setTopFlightsHome]);

  const handleSetReturnOneWay = useCallback((type) => {
    setToggleOneWay(false);
    setReturnOrOneWayTopLocations(type);
    setTopFlightsHome(topFlightsHome?.tab, true);
  }, [setReturnOrOneWayTopLocations, setTopFlightsHome, topFlightsHome?.tab]);

  const handlePassengersChange = useCallback((type, value) => {
    setTopLocationsPassengers(type, value);
    setTopFlightsHome(topFlightsHome?.tab, true);
  }, [setTopLocationsPassengers, setTopFlightsHome, topFlightsHome?.tab]);

  const handleLocationClick = useCallback(async (formattedLocation) => {
    const dateFrom = moment().add(10,'days').format("DD/MM/yyyy");
    const dateTo = moment().add(15,'days').format("DD/MM/yyyy");
    
    localStorage.setItem(
      "search",
      JSON.stringify({
        flyFrom: formattedLocation?.flyFrom,
        flyTo: formattedLocation?.flyTo,
        dateFrom,
        dateTo,
        adults: topLocationsPassengers?.adult,
        cityNameFrom: "Pristina",
        cityNameTo: formattedLocation?.city,
        aTimeFrom: "0:00",
        aTimeTo: "24:00",
        selectedClass: "M",
        limit: 30,
        returnFrom: returnOrOneWayTopLocations === "oneway" ? null : dateTo,
        returnTo: returnOrOneWayTopLocations === "oneway" ? null : dateTo,
        returnOrOneWay: returnOrOneWayTopLocations,
      })
    );
    
    navigate(`/search?session=${nanoid()}`);
  }, [topLocationsPassengers?.adult, returnOrOneWayTopLocations, navigate]);

  const { flight } = useStaticImages();

  // Destination card component with aggressive lazy loading
  const DestinationCard = ({ formattedLocation, index }) => {
    const { isLoaded, imgRef } = useLazyBackgroundImage(formattedLocation?.image, isMobileDevice, index);
    
    // ALL images are lazy loaded to ensure heading is LCP
    const shouldShowLoadingState = !isLoaded;
    
    return (
      <a
        ref={imgRef}
        style={{ cursor: "pointer" }}
        className="col-lg-4 col-md-6 col-12"
        key={`${uniqueId}-${index}`}
        onClick={() => handleLocationClick(formattedLocation)}
        role="button"
        tabIndex={0}
        aria-label={`Search flights to ${formattedLocation?.cityName || "destination"}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleLocationClick(formattedLocation);
          }
        }}
      >
        <Fragment>
          <div className="package-box">
            <figure
              style={{
                background: isLoaded
                  ? `url(${formattedLocation?.image}) 50% 50% no-repeat`
                  : '#f0f0f0',
                backgroundSize: 'cover',
                width: '100%',
                height: '200px',
                willChange: 'auto',
              }}
              role="img"
              aria-label={`${formattedLocation?.cityName || "City"} destination image`}
            >
              {shouldShowLoadingState && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#666',
                  fontSize: '14px'
                }}>
                  Loading...
                </div>
              )}
            </figure>
            <span className="tag">
              {formattedLocation?.cityName || "City"}
            </span>
          </div>
        </Fragment>
      </a>
    );
  };

  return (
    <Fragment>
      {/* Preconnect links for performance */}
      <link rel="preconnect" href="https://images.pexels.com" />
      <link rel="preconnect" href="https://cdn.pixabay.com" />
      <link rel="dns-prefetch" href="https://images.pexels.com" />
      <link rel="dns-prefetch" href="https://cdn.pixabay.com" />
      
      {/* Removed preload to ensure heading is LCP, not images */}

      <div className="row ml-auto mr-auto w-100">
        <div className={"col-lg-12 linksbar d-flex align-items-center col-md-12 col-12"} style={{
          borderBottom: isNotMobile ? "none" : "1px solid #eaeaea"
        }}>
          {isMobileDevice && (
            <Fragment>
              <Accordion sx={{ width: "100%" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  aria-label="Select destination type"
                >
                  <Typography style={{ color: '#003663' }}>
                    <LocationOnOutlinedIcon style={{ color: '#003663' }} />{" "}
                    {capitalizeFirstLetter(topFlightsHome?.tab)}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <ul className="popular_list">
                    <li
                      onClick={handleSetPopular}
                      className={topFlightsHome?.tab === "popular" ? "selected" : ""}
                      style={{ color: topFlightsHome?.tab === "popular" ? "" : '#003663' }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={topFlightsHome?.tab === "popular"}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSetPopular();
                        }
                      }}
                    >
                      Popular
                    </li>
                  </ul>
                </AccordionDetails>
              </Accordion>
              
              {/* Flight type selector */}
              <Accordion sx={{ width: "100%" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2a-content"
                  id="panel2a-header"
                  aria-label="Select flight type"
                >
                  <Typography style={{ color: '#003663' }}>
                    <img 
                      srcSet={flight} 
                      alt="flight" 
                      width={20} 
                      height={20}
                      loading="lazy"
                    />{" "}
                    {returnOrOneWayTopLocations === "oneway"
                      ? "One Way"
                      : "Round Trip"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <ul className="popular_list">
                    <li
                      onClick={() => handleSetReturnOneWay("return")}
                      className={
                        returnOrOneWayTopLocations === "return" ||
                        !returnOrOneWayTopLocations
                          ? "selected"
                          : ""
                      }
                      style={{ color: (returnOrOneWayTopLocations === "return" || !returnOrOneWayTopLocations) ? "" : '#003663' }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={returnOrOneWayTopLocations === "return" || !returnOrOneWayTopLocations}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSetReturnOneWay("return");
                        }
                      }}
                    >
                      Round Trip
                    </li>
                    <li
                      onClick={() => handleSetReturnOneWay("oneway")}
                      className={
                        returnOrOneWayTopLocations === "oneway"
                          ? "selected"
                          : ""
                      }
                      style={{ color: returnOrOneWayTopLocations === "oneway" ? "" : '#003663' }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={returnOrOneWayTopLocations === "oneway"}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSetReturnOneWay("oneway");
                        }
                      }}
                    >
                      One Way
                    </li>
                  </ul>
                </AccordionDetails>
              </Accordion>
              
              {/* Travelers selector */}
              <Accordion sx={{ width: "100%" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel3a-content"
                  id="panel3a-header"
                  aria-label="Select number of travelers"
                >
                  <Typography style={{ color: '#003663' }}>
                    <img
                      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAcCAYAAAB75n/uAAAACXBIWXMAAAsTAAALEwEAmpwYAAACe0lEQVRIia3WT6hVVRQG8N+7p1cT0URLaWCPogwnCdGfI2gqOagwCkKkQJPyTLSMKChBbGCSIhYSSQelSVlCBYaQYOUkORpo4EAsUFADiwKFsEI75WCv83xd3r3cq/ebfHtv9v6+tffZa+0zVNe18ZDlRdO8C69iKSbG2I/4AO+Muxh1VYJWpwmBAj8FX8Y3+A4zsRWncXc3gW4Gz0WUv2EhpuIRzMUwXsEMHMPkvgyyvJiOD/E7bseBtin/SMfzKG7C3n538Ebwk/ir02Lswy7Mwex+DJbjIg52EW+wKfjpfgwm4UQP4nAueEY/BnT5cG0YDv67H4MjuKNHg3nBh/sxeDu4YyKNQTPn054N6qr8DEfxspRk42EIX2I6VkmXojeDwHyckZJtv3RlZ+FerMZ5LEaJ9zuJ3NDF4A8pyT7Cs1IWt+NFvNdFw1B7scvy4s6IfgK24xKm4SncE/1D+CKWTMYy6YgO4CRXi93/DLK8eBdr2oJ4ATs7BLhSOqKx2IY1jcHoEWV58TGeQYXNyGLxDmyRatMPMX02VkT0FyKIGq/hJdwSWmkHWV4swLf4qq7Kx8KQdAnW43Xc2BbpZalMvBniDfbicSyqq/LrZgfNESxuE/k3DNbjPleT75SUjOPhiTDcgZEhDzw/Db/g87oqRwvWmBftWrAbS3BbCw/H4K7rUWzDJ8HzW3goOscHaNBo3d+S3lc4O0CDn4NHWpgSnT8HaNC8gje3RD2vq/K/ARo0WsMtcd2yvOi1/veCkeDjLen/BvZkeTF1AOJTsCfa25pMXou3YvB7/HqN4rfiwWivq6tyw2ixy/JiCdZK9f56cAwb66rcDVcA8U+c682/nmgAAAAASUVORK5CYII="
                      alt="Travelers icon"
                      width="15"
                      height="15"
                      loading="lazy"
                      style={{ marginRight: '12px' }}
                    />
                    Travelers
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ul className="popular_list">
                    <li className="flex-row justify-content-space-between" style={{ color: '#003663' }}>
                      <span style={{ color: '#003663', fontWeight: 'bold' }}>ADULTS</span>
                      <br />
                      <span style={{ color: '#003663' }}>12 years and older</span>
                      <div className="quantity">
                        <button
                          className="disabled"
                          style={{ 
                            color: topLocationsPassengers?.adult > 1 ? '#003663' : '#ccc',
                            backgroundColor: topLocationsPassengers?.adult > 1 ? '#fbd215' : '#f5f5f5',
                            border: `1px solid ${topLocationsPassengers?.adult > 1 ? '#fbd215' : '#ccc'}`,
                            cursor: topLocationsPassengers?.adult > 1 ? 'pointer' : 'not-allowed'
                          }}
                          onClick={() => {
                            if (topLocationsPassengers?.adult > 1) {
                              handlePassengersChange("adult", topLocationsPassengers?.adult - 1);
                            }
                          }}
                          disabled={topLocationsPassengers?.adult <= 1}
                          aria-label="Decrease number of adult passengers"
                        >
                          -
                        </button>
                        <input
                          data-value="true"
                          type="text"
                          readOnly
                          value={topLocationsPassengers?.adult}
                          disabled
                          style={{ 
                            backgroundColor: 'white',
                            color: '#003663',
                            border: '1px solid #e0e0e0',
                            textAlign: 'center'
                          }}
                          aria-label="Number of adult passengers"
                        />
                        <button
                          className="active"
                          style={{ 
                            color: '#003663', 
                            backgroundColor: '#fbd215',
                            border: '1px solid #fbd215',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            handlePassengersChange("adult", topLocationsPassengers?.adult + 1);
                          }}
                          aria-label="Increase number of adult passengers"
                        >
                          +
                        </button>
                      </div>
                    </li>
                  </ul>
                </AccordionDetails>
              </Accordion>
            </Fragment>
          )}

          {isNotMobile && (
            <Fragment>
              <div className="leftlinks">
                <ul>
                  <li>
                    <a
                      className={topFlightsHome?.tab === "popular" ? "active" : ""}
                      onClick={handleSetPopular}
                      role="button"
                      tabIndex={0}
                      aria-pressed={topFlightsHome?.tab === "popular"}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSetPopular();
                        }
                      }}
                    >
                      Popular
                    </a>
                  </li>
                </ul>
              </div>
              
              {/* Desktop controls */}
              <div className="rightdiv ml-auto">
                <div className="dropdown normal-drop">
                  <button
                    className="btn btn-secondary dropdown-toggle"
                    type="button"
                    data-toggle="dropdown"
                    aria-expanded="false"
                    aria-label="Select flight type"
                    onClick={handleToggleOneWay}
                  >
                    <img 
                      src={require("../assets/img/flight1.png")} 
                      alt="Flight search icon" 
                      width="20"
                      height="20"
                      loading="lazy"
                    />
                    {" "}
                    {returnOrOneWayTopLocations === "oneway"
                      ? "One-Way"
                      : "Round trip"}
                    <img
                      src={require("../assets/img/down-arrow-blue.png")}
                      alt="Dropdown arrow"
                      width="12"
                      height="8"
                      loading="lazy"
                    />
                  </button>
                  <div
                    className={`dropdown-menu bluedrop ${
                      toggleOneWay ? "show" : ""
                    }`}
                    class="collapse"
                    style={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0' }}
                  >
                    <button
                      className="dropdown-item"
                      style={{ 
                        color: '#003663',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#003663';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#003663';
                      }}
                      onClick={() => {
                        handleToggleOneWay();
                        handleSetReturnOneWay("oneway");
                      }}
                      aria-label="Select one-way flight"
                    >
                      One-Way
                    </button>
                    <button
                      className="dropdown-item"
                      style={{ 
                        color: '#003663',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#003663';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#003663';
                      }}
                      onClick={() => {
                        handleToggleOneWay();
                        handleSetReturnOneWay("return");
                      }}
                      aria-label="Select round trip flight"
                    >
                      Round Trip
                    </button>
                  </div>
                </div>
                
                <div className="normal-drop position-relative">
                  <div className="dropdown normal-drop">
                    <button
                      className="btn btn-secondary dropdown-toggle"
                      type="button"
                      data-toggle="dropdown"
                      aria-expanded="false"
                      aria-label="Select number of travelers"
                      onClick={handleTogglePassengers}
                      style={{ minWidth: '140px' }}
                    >
                      <img
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAcCAYAAAB75n/uAAAACXBIWXMAAAsTAAALEwEAmpwYAAACe0lEQVRIia3WT6hVVRQG8N+7p1cT0URLaWCPogwnCdGfI2gqOagwCkKkQJPyTLSMKChBbGCSIhYSSQelSVlCBYaQYOUkORpo4EAsUFADiwKFsEI75WCv83xd3r3cq/ebfHtv9v6+tffZa+0zVNe18ZDlRdO8C69iKSbG2I/4AO+Muxh1VYJWpwmBAj8FX8Y3+A4zsRWncXc3gW4Gz0WUv2EhpuIRzMUwXsEMHMPkvgyyvJiOD/E7bseBtin/SMfzKG7C3n538Ebwk/ir02Lswy7Mwex+DJbjIg52EW+wKfjpfgwm4UQP4nAueEY/BnT5cG0YDv67H4MjuKNHg3nBh/sxeDu4YyKNQTPn054N6qr8DEfxspRk42EIX2I6VkmXojeDwHyckZJtv3RlZ+FerMZ5LEaJ9zuJ3NDF4A8pyT7Cs1IWt+NFvNdFw1B7scvy4s6IfgK24xKm4SncE/1D+CKWTMYy6YgO4CRXi93/DLK8eBdr2oJ4ATs7BLhSOqKx2IY1jcHoEWV58TGeQYXNyGLxDmyRatMPMX02VkT0FyKIGq/hJdwSWmkHWV4swLf4qq7Kx8KQdAnW43Xc2BbpZalMvBniDfbicSyqq/LrZgfNESxuE/k3DNbjPleT75SUjOPhiTDcgZEhDzw/Db/g87oqRwvWmBftWrAbS3BbCw/H4K7rUWzDJ8HzW3goOscHaNBo3d+S3lc4O0CDn4NHWpgSnT8HaNC8gje3RD2vq/K/ARo0WsMtcd2yvOi1/veCkeDjLen/BvZkeTF1AOJTsCfa25pMXou3YvB7/HqN4rfiwWivq6tyw2ixy/JiCdZK9f56cAwb66rcDVcA8U+c682/nmgAAAAASUVORK5CYII="
                        alt="Travelers icon"
                        width="15"
                        height="15"
                        loading="lazy"
                      />
                      <span style={{ display: 'inline-block', minWidth: '20px', textAlign: 'center' }}>
                        {Number(topLocationsPassengers.adult) +
                          Number(topLocationsPassengers.children) +
                          Number(topLocationsPassengers.babies)}
                      </span> Traveler(s)
                      <img
                        src={require("../assets/img/down-arrow-blue.png")}
                        alt="Dropdown arrow"
                        width="12"
                        height="8"
                        loading="lazy"
                      />
                    </button>
                    <div className={`dropdown-menu bluedrop ${togglePassengers ? 'show' : ''}`} style={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0' }}>
                      <div className="px-3 pt-3 pb-2">
                        <h6 className="mb-2" style={{ color: '#003663', fontWeight: 'bold' }}>
                          Passengers
                        </h6>
                        <hr className="mb-2" style={{ borderColor: '#e0e0e0' }} />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <div style={{ color: '#003663', fontWeight: 'bold' }}>ADULTS</div>
                            <small style={{ color: '#003663' }}>12 years and older</small>
                          </div>
                          <div className="quantity">
                            <button
                              className="disabled"
                              style={{ 
                                color: topLocationsPassengers?.adult > 1 ? '#003663' : '#ccc',
                                backgroundColor: topLocationsPassengers?.adult > 1 ? '#fbd215' : '#f5f5f5',
                                border: `1px solid ${topLocationsPassengers?.adult > 1 ? '#fbd215' : '#ccc'}`,
                                cursor: topLocationsPassengers?.adult > 1 ? 'pointer' : 'not-allowed'
                              }}
                              onClick={() => {
                                if (topLocationsPassengers?.adult > 1) {
                                  handlePassengersChange("adult", topLocationsPassengers?.adult - 1);
                                }
                              }}
                              disabled={topLocationsPassengers?.adult <= 1}
                              aria-label="Decrease number of adult passengers"
                            >
                              -
                            </button>
                            <input
                              data-value="true"
                              type="text"
                              readOnly
                              value={topLocationsPassengers?.adult}
                              disabled
                              style={{ 
                                backgroundColor: 'white',
                                color: '#003663',
                                border: '1px solid #e0e0e0',
                                textAlign: 'center'
                              }}
                              aria-label="Number of adult passengers"
                            />
                            <button
                              className="active"
                              style={{ 
                                color: '#003663', 
                                backgroundColor: '#fbd215',
                                border: '1px solid #fbd215',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                handlePassengersChange("adult", topLocationsPassengers?.adult + 1);
                              }}
                              aria-label="Increase number of adult passengers"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>
          )}
        </div>
      </div>

      <div className="row package-space">
        {formattedTopLocations?.length ? (
          formattedTopLocations?.map((formattedLocation, index) =>
            formattedLocation?.cityName ? (
              <DestinationCard 
                key={`dest-${index}`}
                formattedLocation={formattedLocation} 
                index={index} 
              />
            ) : null
          )
        ) : (
          <NoResultsFound />
        )}
      </div>
    </Fragment>
  );
}; 